import {Component, OnInit, AfterViewInit, Inject, ViewChild} from '@angular/core';
import {PreferencesService} from '../../services/preferences.service';
import {HasSubscriptions} from '../has-subscriptions';
import {ListingsService} from '../../services/listings.service';
import {Pagination} from '../../classes/pagination';
import {Listing} from '../../classes/listing';
import {CommunicationService} from '../../services/communication.service';
import {ListingCategory, ListingTypeMap, SCREEN_SIZE} from '../../classes/enums';
import {MetaTagService, OpenGraphTag} from '../../services/meta-tag.service';
import {ActivatedRoute} from '@angular/router';
import {Filters} from '../../classes/user-preferences';
import {RoutingHistoryService} from '../../services/routing-history.service';
import {HistoryItem} from '../../classes/history-item';
import {LandingPageConfigResponse} from '../../resolvers/page-config-resolver';
import { TranslateService } from '@ngx-translate/core';
import { OdometerService } from '../../services/odometer.service';
import { UserService } from '../../services/user.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { NOTIFICAITON_EMAIL_UNSUBSCRIBE } from '../../classes/analytics-tags';
import { BlogService } from '../../services/blog.service';
import sortBy from 'lodash-es/sortBy';
import { IBlogResponse } from '../../classes/interfaces';
import { PreRenderService } from '../../services/pre-render.service';
import { debounceTime, take } from 'rxjs/operators';
import get from 'lodash-es/get';
import { StaticPathPipe } from '../../shared/pipes/static-path.pipe';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { BlogPostSourcePage } from '../../classes/enums';
import {SearchBarComponent} from "../search-bar/search-bar.component";

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss', './landing-page.desktop.component.scss']
})
export class LandingPageComponent extends HasSubscriptions implements OnInit, AfterViewInit {
  searchIcon: string;
  bannerUrl: string;
  bannerTitleFirstPart: string;
  bannerTitleSecondPart: string;
  videoUrl: SafeResourceUrl;
  campaignUrl: string;
  isLuxury: boolean;
  isCommercial: boolean;
  isResidential: boolean;
  showUnsubscribedModal: boolean = false;
  province: string;
  pageConfig: LandingPageConfigResponse;
  screenSize: SCREEN_SIZE;
  pagination: Pagination = new Pagination();
  suggestedListings: Array<Listing> = [];
  totalListings: number;
  posts: IBlogResponse[];
  SCREEN_SIZE = SCREEN_SIZE;
  BlogPostSourcePage = BlogPostSourcePage;
  h1Text: string = '';
  productSchema = [];
  showBannerStyle = {};
  showVideoStyle = {};
  isScreenSizeGreaterThanTablet : boolean = false;
  personas = [{
      url: '/persona/my-first-home',
      image: '/assets/landing_page/persona1.jpg',
      name: 'landing-page-residential.personas.my-first-home'
    }, {
      url: '/persona/our-first-home',
      image: '/assets/landing_page/persona2.jpg',
      name: 'landing-page-residential.personas.our-first-home'
    }, {
      url: '/persona/our-next-home',
      image: '/assets/landing_page/persona3.jpg',
      name: 'landing-page-residential.personas.our-next-home'
    }, {
      url: '/persona/our-right-sized-home',
      image: '/assets/landing_page/persona4.jpg',
      name: 'landing-page-residential.personas.our-right-sized-home'
    }, {
      url: '/persona/our-luxury-home',
      image: '/assets/landing_page/persona5.jpg',
      name: 'landing-page-residential.personas.our-luxury-home'
    }, {
      url: '/persona/our-multi-family-home',
      image: '/assets/landing_page/persona6.jpg',
      name: 'landing-page-residential.personas.our-multi-family-home'
  }];

  tdAd: string;

  @ViewChild('appSearchBar') searchBar: SearchBarComponent;

  constructor(
    private preferencesService: PreferencesService,
    private listingsService: ListingsService,
    private communicationService: CommunicationService,
    private metaTagsService: MetaTagService,
    private activatedRoute: ActivatedRoute,
    private odometerService: OdometerService,
    private prerenderService: PreRenderService,
    private historyService: RoutingHistoryService,
    private blogService: BlogService,
    private translateService: TranslateService,
    private staticPathPipe: StaticPathPipe,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private sanitizer: DomSanitizer
  ) {
    super();
  }

  ngAfterViewInit() {
    if (this.isResidential) {
      this.odometerService.init('.odometer', 0, this.totalListings || 75000);
    }
  }

  ngOnInit() {
    // Handles reinitialization when switching between province pages.
    this.addSub(this.activatedRoute.data.subscribe(data => {
      this.isCommercial = data.isCommercial;
      this.isLuxury = data.isLuxury;
      this.province = data.province;
      this.isResidential = !this.isCommercial && !this.isLuxury;
      this.searchIcon = this.getSearchIcon()
      this.pageConfig = data.pageConfig;
      this.bannerUrl = !!this.pageConfig ? this.pageConfig.heroImageUrl : this.getBannerUrl();
      this.setVideoUrl();
      this.campaignUrl = !!this.pageConfig && this.pageConfig.heroCampaignImageUrl ? this.pageConfig.heroCampaignImageUrl : this.getCampaignBannerUrl();
      this.showBannerStyle = this.addStyles(this.bannerUrl);
      this.showVideoStyle = this.addStyles(this.campaignUrl);

      if (this.isCommercial) this.communicationService.setCurrentListingType(ListingCategory.COMMERCIAL, false);
      else if (this.isLuxury) this.communicationService.setCurrentListingType(ListingCategory.LUXURY, false);
      else this.communicationService.setCurrentListingType(ListingCategory.RESIDENTIAL, false);

      if (this.province) {
        const filters = new Filters();
        filters.featuredListings = this.isCommercial ? 'c' : this.isLuxury ? 'l' : 'r';
        this.listingsService.searchProvinceCity(this.province, null, this.pagination, filters).toPromise().then((result) => {
          this.suggestedListings = result.results;
        });
      }
      else if (this.prerenderService.isBrowserMode) {
        this.preferencesService.centerMapAtCurrentLocation();
        this.preferencesService.doneGettingPhysicalLocation.pipe(debounceTime(100), take(1)).toPromise().then((e) => this._getListings(e));
      }

      if (this.isResidential) {
        this.listingsService.getListingsCount().toPromise().then(count => {
          // Cache count in server version, but don't render it.
          if (this.prerenderService.isBrowserMode) this.totalListings = count;
        });

        this.blogService.getByTags(['cms-homepage-1', 'cms-homepage-2', 'cms-homepage-3', 'cms-listing-details-TD']).toPromise()
          .then((results) => {
            if (results) {
              const tdAd = results.filter((obj) => obj.tag === 'cms-listing-details-TD');
              this.tdAd = get(tdAd, '[0].content.content.rendered');
            }
          });

      }
      this.communicationService.footerLinksChange.next({ city: null, province: data.province });
      this._setSEOItems();
    }));

    this.addSub(this.communicationService.screenSize.subscribe((screenSize) => {
      this.screenSize = screenSize;
      this.isScreenSizeGreaterThanTablet = screenSize >= SCREEN_SIZE.TABLET_PORTRAIT_UP;
      this.showBannerStyle = this.addStyles(this.bannerUrl);
      this.showVideoStyle = this.addStyles(this.campaignUrl);
      this.setBannerTitle();
    }));
    this.historyService.push(new HistoryItem());

    if (this.activatedRoute.snapshot.data.unsubscribed) {
      this.userService.unsubscribeSavedSearchNotifications(this.activatedRoute.snapshot.data.unsubscribed)
        .subscribe(() => {
          this.analyticsService.eventTrack(NOTIFICAITON_EMAIL_UNSUBSCRIBE, null);
          this.userService.currentUser.subscribe(user => {
              if (user) {
                  user.preferences.savedSearches.forEach(saveSearch => {
                      saveSearch.notificationsEnabled = false;
                    });
                  this.userService.saveUserPreferences(user.preferences);
                }
            });
          this.showUnsubscribedModal = true;
        });
    }
  }

  getBannerUrl(): string {
    if (this.isCommercial) return '/assets/landing_page/commercial_hero@3x.jpg';
    if (this.isLuxury) return '/assets/landing_page/luxury_hero@3x.jpg';
    return '/assets/landing_page/hero_image@3x.jpg';
  }

  setVideoUrl() {
    const videoUrlArray = !!this.pageConfig && this.pageConfig.heroVideoUrl ? this.pageConfig.heroVideoUrl : [];
    if (videoUrlArray.length) {
      // pick a random url
      const random = Math.floor(Math.random() * (videoUrlArray.length - 1)) + 0;
      this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pageConfig.heroVideoUrl[random]);
    }
    else {
      this.videoUrl =  this.getVideoUrl();
    }
  }

  getVideoUrl(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl('https://www.youtube.com/embed/arhajOkf_K4?rel=0');
  }

  getCampaignBannerUrl(): string {
    return '/assets/landing_page/remax-campaign.jpg'
  }

  setBannerTitle() {
    this.screenSize === SCREEN_SIZE.MOBILE_ONLY ? this.getBannerTitleFirstPart('landing-page-residential.mobile.with-over')
                                                                             : this.getBannerTitleFirstPart('landing-page-residential.with-over');
    this.screenSize === SCREEN_SIZE.MOBILE_ONLY ? this.getBannerTitleSecondPart('landing-page-residential.mobile.listings-across-canada')
                                                                              : this.getBannerTitleSecondPart('landing-page-residential.listings-across-canada');
  }

  getBannerTitleFirstPart(key: string) {
    this.translateService.get(key).toPromise().then((description) => this.bannerTitleFirstPart = description);
  }

  getBannerTitleSecondPart(key: string) {
    this.translateService.get(key).toPromise().then((description) => this.bannerTitleSecondPart = description);
  }

  centerMapAtUserLocation() {
    this.preferencesService.centerMapAtCurrentLocation(false, true);
  }

  addStyles(url: string) {
    const statURL = this.staticPathPipe.transform(url +  '?auto=compress,format&q=25');
    if (this.screenSize === SCREEN_SIZE.MOBILE_ONLY) {
      return {
        'background-image' : 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),url(' + statURL + ')'
        };
    }
    return {
      'background-image' : 'url(' + statURL + ')'
    };
  }

  private getSearchIcon() {
    if (this.isCommercial || this.isLuxury) return '/assets/landing_page/search_icon@1x.svg';
    return '/assets/landing_page/search_icon_residential@1x.svg';
  }

  private _setSEOItems() {
    let key = 'meta-tags.';
    let vars = {};
    if (this.province) {
      key += 'province-page';
      vars = {
        province: this.province
      };
    }
    else key += 'landing-' + (this.isCommercial ? 'commercial' : (this.isLuxury ? 'luxury' : 'residential'));


    const openGraphTags: OpenGraphTag[] = [
      {
        name: 'title',
        content: key + '.title',
        variables: vars,
        isTranslateKey: true
      },
      {
        name: 'description',
        content: key + '.description',
        variables: vars,
        isTranslateKey: true
      },
    ];
    if (this.province) openGraphTags.push({ name: 'image', content: this.bannerUrl});

    this.metaTagsService.setPageDescription(key + '.description', vars);
    this.metaTagsService.setPageTitle(key + '.title', vars);
    this.metaTagsService.setOpenGraphTags(openGraphTags);
    this._setH1Text(key + '.h1', vars);
  }

  private async _setH1Text(translationName: string, variables: {}) {
    if (variables) {
      if (variables['province']) variables['province'] = await this.translateService.get('provinces.' + variables['province']).toPromise();
    }
    this.translateService.get(translationName, variables).toPromise().then((description) => this.h1Text = description);
  }

  /**
   * Retrieve listings to display
   * 1) if we know the location we'll get location specific listings
   * 2) if we dont know the location, we'll get canadian wide listings
   */
  private _getListings(e: boolean[]) {
    const filters = new Filters();
    let distance = 100000;
    filters.featuredListings = this.isCommercial ? 'c' : this.isLuxury ? 'l' : 'r';
    if (this.isCommercial) {
      filters.customListingTypeIds = [...ListingTypeMap.commercial];
    }
    if (this.isLuxury) {
      this.pagination.limit = 2;
      distance = 10000000; // not so arbitrarily high number
    }
    if (e[0]) {
      this.preferencesService.positionInfoChanged.pipe(take(1)).toPromise().then((newPosition) => {
        this.listingsService.getListingsByDistanceFrom(this.pagination, filters, newPosition.centerLatLng, distance).then((listings) => {
          this.suggestedListings = listings.results;
          this.productSchema = this.listingsService.checkForProductValues(this.suggestedListings);
        });
      });
    }
    else {
      this.listingsService.searchProvinceCity(null, null, this.pagination, filters).pipe(take(1)).toPromise().then((listings) => {
        this.suggestedListings = listings.results;
        this.productSchema = this.listingsService.checkForProductValues(this.suggestedListings);
      });
    }
  }

}
